// The maths behind the price chart, kept apart from the component that draws it
// so it can be reasoned about (and tested) without a DOM.
//
// Everything here is pure: points in, numbers out.

import type { PricePoint } from "@/lib/types";

export type Range = "3m" | "1y" | "all";

export const RANGES: { key: Range; label: string; days: number | null }[] = [
  { key: "3m", label: "3 months", days: 92 },
  { key: "1y", label: "1 year", days: 366 },
  { key: "all", label: "All", days: null },
];

/**
 * Parse a bare YYYY-MM-DD into a timestamp.
 *
 * The explicit T00:00:00Z matters. `new Date("2026-08-16")` is parsed as UTC but
 * `new Date("2026-08-16 00:00")` is parsed as local time, and mixing the two
 * shifts points by a day for anyone west of Greenwich.
 */
export function dayToTime(d: string): number {
  return Date.parse(`${d}T00:00:00Z`);
}

/** Points inside the range, oldest first. Assumes the input is already sorted. */
export function sliceRange(points: PricePoint[], range: Range): PricePoint[] {
  const days = RANGES.find((r) => r.key === range)?.days ?? null;
  if (days === null) return points;
  const cutoff = Date.now() - days * 86_400_000;
  return points.filter((p) => dayToTime(p.d) >= cutoff);
}

export type Scales = {
  x: (d: string) => number;
  y: (price: number) => number;
  yTicks: number[];
  bounds: { min: number; max: number; first: string; last: string };
};

export type Box = {
  width: number;
  height: number;
  padTop: number;
  padRight: number;
  padBottom: number;
  padLeft: number;
};

/**
 * Scales for a set of points inside a viewBox.
 *
 * The y domain is never anchored at zero. Laptop prices move by a few percent,
 * and against a zero baseline every series renders as a flat line across the top
 * of the box. It is padded instead, and given a floor so a genuinely unchanged
 * price does not divide by zero.
 *
 * x is a time scale rather than an index scale: refresh runs get skipped, and
 * spacing skipped weeks evenly would draw a lie.
 */
export function buildScales(points: PricePoint[], box: Box): Scales {
  const innerW = box.width - box.padLeft - box.padRight;
  const innerH = box.height - box.padTop - box.padBottom;

  const prices = points.map((p) => p.p);
  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);

  let min = rawMin;
  let max = rawMax;
  if (max - min < 1) {
    // A flat price still deserves a visible line, drawn through the middle.
    min = rawMin - 5;
    max = rawMax + 5;
  } else {
    const pad = (max - min) * 0.12;
    min -= pad;
    max += pad;
  }

  const tMin = dayToTime(points[0].d);
  const tMax = dayToTime(points[points.length - 1].d);
  const span = tMax - tMin;

  return {
    x: (d: string) =>
      box.padLeft + (span === 0 ? innerW / 2 : ((dayToTime(d) - tMin) / span) * innerW),
    y: (price: number) =>
      box.padTop + (1 - (price - min) / (max - min)) * innerH,
    yTicks: niceTicks(min, max, 4),
    bounds: {
      min: rawMin,
      max: rawMax,
      first: points[0].d,
      last: points[points.length - 1].d,
    },
  };
}

/** Round tick values inside a domain, for the horizontal grid lines. */
export function niceTicks(min: number, max: number, count: number): number[] {
  const span = max - min;
  if (span <= 0) return [min];
  const rough = span / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10]
    .map((m) => m * magnitude)
    .find((s) => s >= rough) ?? magnitude * 10;

  const ticks: number[] = [];
  for (let t = Math.ceil(min / step) * step; t <= max; t += step) {
    ticks.push(Math.round(t * 100) / 100);
  }
  return ticks;
}

/** An SVG path through the points. Straight segments only — a smoothed curve
 *  would invent prices on dates nobody looked. */
export function linePath(points: PricePoint[], s: Scales): string {
  return points
    .map((p, i) => `${i ? "L" : "M"}${s.x(p.d).toFixed(1)} ${s.y(p.p).toFixed(1)}`)
    .join(" ");
}

/** The line path closed down to the baseline, for the area fill underneath. */
export function areaPath(points: PricePoint[], s: Scales, baselineY: number): string {
  if (!points.length) return "";
  const last = points[points.length - 1];
  return (
    `${linePath(points, s)} ` +
    `L${s.x(last.d).toFixed(1)} ${baselineY.toFixed(1)} ` +
    `L${s.x(points[0].d).toFixed(1)} ${baselineY.toFixed(1)} Z`
  );
}

/** The point nearest an x position, for hover. */
export function nearestIndex(points: PricePoint[], s: Scales, atX: number): number {
  let best = 0;
  let bestDistance = Infinity;
  points.forEach((p, i) => {
    const distance = Math.abs(s.x(p.d) - atX);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  });
  return best;
}

/** "17 Aug" / "17 Aug 2026" when the year is not the current one. */
export function formatDay(d: string, withYear = false): string {
  const date = new Date(dayToTime(d));
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}
